import { Star, FileText, Clock, MapPin, CheckCircle, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useReveal } from "@/hooks/useReveal";

const features = [
  { icon: Star, title: "Intelligent pricing", body: "Price your home with real comparable sales data and market trends — never guesswork." },
  { icon: FileText, title: "Contract guidance", body: "Upload any contract and we break it down in plain language — what it means, what to push back on." },
  { icon: Clock, title: "Experts on demand", body: "Need an attorney, inspector, or title company? Real professionals, available when you need them." },
  { icon: MapPin, title: "Verified listings", body: "Every listing is seller-verified with inspection history, price transparency, and community reviews." },
  { icon: CheckCircle, title: "Offer coaching", body: "We coach you through every offer — what comparable buyers paid and how to put your best foot forward." },
  { icon: Users, title: "Trust layer", body: "Every transaction leaves a verified review. Sellers build trust scores. Buyers see real histories." },
];

const FeatureCard = ({
  feature,
  index,
  gridVisible,
}: {
  feature: typeof features[number];
  index: number;
  gridVisible: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const Icon = feature.icon;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => setActive(e.isIntersecting));
      },
      { threshold: 0.55, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group relative bg-ink p-10 transition-all duration-700 scroll-reveal ${gridVisible ? "is-visible" : ""} ${active ? "bg-canvas/[0.03]" : ""}`}
      style={{ transitionDelay: `${index * 0.08}s` }}
    >
      <div
        className={`relative w-12 h-12 rounded-full border flex items-center justify-center mb-7 transition-all duration-500 ${
          active ? "border-gold bg-gold/10" : "border-canvas/15"
        } group-hover:border-gold group-hover:bg-gold/10`}
      >
        <Icon className="h-4.5 w-4.5 text-gold" strokeWidth={1.5} />
      </div>
      <p className="font-display text-2xl font-medium text-canvas mb-3 tracking-tight">{feature.title}</p>
      <p className="text-sm text-canvas/60 leading-[1.7] font-light">{feature.body}</p>

      {/* hairline gold accent — triggers on scroll-in and on hover */}
      <div
        className={`absolute bottom-0 left-10 right-10 h-px bg-gold origin-left transition-transform duration-700 ${
          active ? "scale-x-100" : "scale-x-0"
        } group-hover:scale-x-100`}
      />
    </div>
  );
};

const Features = () => {
  const header = useReveal<HTMLDivElement>();
  const grid = useReveal<HTMLDivElement>(0.1);

  return (
    <section className="py-28 md:py-36 px-8 md:px-12 bg-ink text-canvas relative overflow-hidden">
      {/* ambient warmth */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gold/8 blur-3xl animate-drift" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-wood/10 blur-3xl" />

      <div className="relative max-w-[960px] mx-auto">
        <div
          ref={header.ref}
          className={`max-w-2xl mb-20 scroll-reveal ${header.visible ? "is-visible" : ""}`}
        >
          <div className="inline-flex items-center gap-2.5 mb-6">
            <span className="w-8 h-px bg-gold" />
            <span className="text-[11px] font-medium tracking-[0.25em] uppercase text-gold">
              Why REALV3 works
            </span>
          </div>
          <h2 className="font-display text-5xl md:text-7xl font-light text-canvas leading-[1.0] tracking-tight mb-6">
            Everything an agent does.
            <br />
            <span className="accent-italic text-canvas/75">Minus the 3–6%.</span>
          </h2>
          <p className="text-base md:text-lg text-canvas/65 leading-[1.7] font-light max-w-xl">
            You were never the problem — the lack of support was. We built every tool and connection you need so you never feel alone, and never lose tens of thousands to commission.
          </p>
        </div>

        <div ref={grid.ref} className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-canvas/8 rounded-[20px] overflow-hidden">
          {features.map((f, i) => (
            <FeatureCard key={f.title} feature={f} index={i} gridVisible={grid.visible} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
